import type {
  BogglePlaygroundMeterBurstEffect,
  BogglePlaygroundMeterBurstSettings,
  BogglePlaygroundMeterBurstTrigger,
  BogglePlaygroundMeterSettings,
} from '~/boggle/config/playground'
import type { BoggleMeterState } from '~/boggle/lib/meter'

/** Canonical burst id for the timer-end Gold Rush phase on Race and compatible presets. */
export const BOGGLE_GOLD_RUSH_BURST_ID = 'gold-rush'

export function getEnabledMeterBursts(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
): readonly BogglePlaygroundMeterBurstSettings[] {
  return settings.bursts.filter(burst => burst.enabled)
}

export function findMeterBurstById(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
  burstId: string | null,
): BogglePlaygroundMeterBurstSettings | null {
  if (burstId === null) {
    return null
  }

  return settings.bursts.find(burst => burst.id === burstId) ?? null
}

export function findEnabledMeterBurstByTrigger(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
  trigger: BogglePlaygroundMeterBurstTrigger,
): BogglePlaygroundMeterBurstSettings | null {
  return getEnabledMeterBursts(settings).find(burst => burst.trigger === trigger) ?? null
}

export function findGoldRushBurst(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
): BogglePlaygroundMeterBurstSettings | null {
  return (
    getEnabledMeterBursts(settings).find(burst => burst.id === BOGGLE_GOLD_RUSH_BURST_ID) ??
    null
  )
}

export function usesGoldRushBurst(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
): boolean {
  return findGoldRushBurst(settings) !== null
}

export function isGoldRushBurstActive(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts' | 'enabled'>,
  meter: Pick<BoggleMeterState, 'activeBurstId' | 'burstActive'>,
): boolean {
  if (!settings.enabled) {
    return false
  }

  return (
    meter.burstActive && meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID
  )
}

export function isTimerEndGoldRushBurstConfigured(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
): boolean {
  const goldRushBurst = findGoldRushBurst(settings)
  return goldRushBurst !== null && goldRushBurst.trigger === 'timer-end'
}

export function meterBurstHasEffect(
  burst: Pick<BogglePlaygroundMeterBurstSettings, 'effects'>,
  effect: BogglePlaygroundMeterBurstEffect,
): boolean {
  return burst.effects.includes(effect)
}

export function getActiveMeterBurst(
  settings: Pick<BogglePlaygroundMeterSettings, 'bursts'>,
  meter: Pick<BoggleMeterState, 'activeBurstId' | 'burstActive'>,
): BogglePlaygroundMeterBurstSettings | null {
  if (!meter.burstActive) {
    return null
  }

  return findMeterBurstById(settings, meter.activeBurstId)
}

export function isMeterBurstEffectActive(
  settings: Pick<BogglePlaygroundMeterSettings, 'enabled' | 'bursts'>,
  meter: Pick<BoggleMeterState, 'activeBurstId' | 'burstActive'>,
  effect: BogglePlaygroundMeterBurstEffect,
): boolean {
  if (!settings.enabled) {
    return false
  }

  const activeBurst = getActiveMeterBurst(settings, meter)
  return activeBurst !== null && meterBurstHasEffect(activeBurst, effect)
}
